import React from 'react';
import axios from 'axios';
import { Send, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { CONTACT } from '@/constants/testIds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: '', email: '', company: '', phone: '', budget: '', message: '' };

export default function ContactForm() {
  const [form, setForm] = React.useState(initial);
  const [loading, setLoading] = React.useState(false);
  const [sent, setSent] = React.useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Please fill in your name, email, and message.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form, { headers: { 'Content-Type': 'application/json' } });
      setSent(true);
      setForm(initial);
      toast.success('We got it. A Sellrly operator will be in touch within one business day.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = 'Something went wrong sending your message. Please try again.';
      if (Array.isArray(detail) && detail[0]?.msg) {
        msg = detail[0].msg.replace(/^value is not a valid email address:?\s*/i, 'Please enter a valid email address. ');
      } else if (typeof detail === 'string') {
        msg = detail;
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div
        id="contact-form"
        data-testid={CONTACT.success}
        className="rounded-2xl p-10 md:p-12 bg-[#0E103D] border border-white/10 text-white"
      >
        <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-[#FF5081]/12 text-[#FF5081] border border-[#FF5081]/25">
          <CheckCircle2 className="w-6 h-6" />
        </div>
        <h3 className="mt-6 font-display text-2xl md:text-3xl font-semibold tracking-[-0.01em]">We got it.</h3>
        <p className="mt-3 text-white/65 leading-relaxed text-[15px]">
          Thanks for reaching out. A Sellrly operator will be in touch within one business day to lock in your
          strategy call. While you wait — read the case study where we hit a $39.35 eCPA against a $100 target in
          two weeks.
        </p>
        <button
          onClick={() => setSent(false)}
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white border border-white/15 hover:border-white/35 hover:bg-white/[0.04]"
          data-testid="contact-send-another"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form
      id="contact-form"
      data-testid={CONTACT.form}
      onSubmit={submit}
      className="rounded-2xl p-7 md:p-10 bg-white text-[#031D44] border border-[#031D44]/10"
    >
      <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#FF5081]">Book a strategy call</p>
      <h3 className="mt-3 font-display text-3xl md:text-[34px] font-semibold tracking-[-0.02em] leading-tight">
        Tell us your goal. We’ll come back with a plan.
      </h3>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Name" required>
          <input
            data-testid={CONTACT.name}
            value={form.name}
            onChange={update('name')}
            type="text"
            required
            placeholder="Jane Founder"
            className="w-full px-4 py-3.5 rounded-2xl border border-[#031D44]/15 bg-white text-[#031D44] placeholder-[#031D44]/35 focus:border-[#FF5081]"
          />
        </Field>
        <Field label="Work email" required>
          <input
            data-testid={CONTACT.email}
            value={form.email}
            onChange={update('email')}
            type="email"
            required
            placeholder="jane@brand.com"
            className="w-full px-4 py-3.5 rounded-2xl border border-[#031D44]/15 bg-white text-[#031D44] placeholder-[#031D44]/35 focus:border-[#FF5081]"
          />
        </Field>
        <Field label="Company">
          <input
            data-testid={CONTACT.company}
            value={form.company}
            onChange={update('company')}
            type="text"
            placeholder="Your brand"
            className="w-full px-4 py-3.5 rounded-2xl border border-[#031D44]/15 bg-white text-[#031D44] placeholder-[#031D44]/35 focus:border-[#FF5081]"
          />
        </Field>
        <Field label="Phone">
          <input
            data-testid={CONTACT.phone}
            value={form.phone}
            onChange={update('phone')}
            type="tel"
            placeholder="(optional)"
            className="w-full px-4 py-3.5 rounded-2xl border border-[#031D44]/15 bg-white text-[#031D44] placeholder-[#031D44]/35 focus:border-[#FF5081]"
          />
        </Field>
        <div className="md:col-span-2">
          <Field label="Monthly media budget">
            <select
              data-testid={CONTACT.budget}
              value={form.budget}
              onChange={update('budget')}
              className="w-full px-4 py-3.5 rounded-2xl border border-[#031D44]/15 bg-white text-[#031D44] focus:border-[#FF5081]"
            >
              <option value="">Select a range</option>
              <option value="<$10k/mo">Under $10k / mo</option>
              <option value="$10k–$50k/mo">$10k – $50k / mo</option>
              <option value="$50k–$250k/mo">$50k – $250k / mo</option>
              <option value="$250k+/mo">$250k+ / mo</option>
              <option value="Not sure yet">Not sure yet</option>
            </select>
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="What number are you trying to hit?" required>
            <textarea
              data-testid={CONTACT.message}
              value={form.message}
              onChange={update('message')}
              required
              rows={5}
              placeholder="Tell us your target CPA / CPC / awareness goal and where you’re running media today."
              className="w-full px-4 py-3.5 rounded-2xl border border-[#031D44]/15 bg-white text-[#031D44] placeholder-[#031D44]/35 focus:border-[#FF5081] resize-none"
            />
          </Field>
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid={CONTACT.submit}
        className="mt-8 inline-flex items-center justify-center gap-2 w-full md:w-auto px-7 py-3.5 rounded-full text-[15px] font-semibold text-white bg-[#FF5081] hover:bg-[#ff3a73] hover:-translate-y-px shadow-[0_10px_28px_-14px_rgba(255,80,129,0.6)] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Sending…
          </>
        ) : (
          <>
            <Send className="w-4 h-4" /> Book my strategy call
          </>
        )}
      </button>

      <p className="mt-4 text-xs text-[#031D44]/55">
        We respect your inbox. No newsletters. We respond within one business day.
      </p>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#031D44]/65 mb-2">
        {label}
        {required && <span className="text-[#FF5081]"> *</span>}
      </span>
      {children}
    </label>
  );
}
